import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API, colors } from "../shared/constants";
import { styles } from "../shared/styles";
import { useIsMobile } from "../shared/utils";
import { InspectionSheet } from "./InspectionSheet";
import { CreateInspection } from "./CreateInspection";
import { LuLock, LuPencil, LuTrash2 } from "react-icons/lu";

export function InspectionsPage({ initialInspectionId = null, onDone = null }) {
  const userRole = localStorage.getItem("sfp_role");
  const isMobile = useIsMobile();
  const [inspections, setInspections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [hovered, setHovered] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [activeInspection, setActiveInspection] = useState(null);
  const [showCompleted, setShowCompleted] = useState(false);
  const openedFromDashboard = useRef(false);

  const loadInspections = () => {
    axios
      .get(`${API}/inspections/`)
      .then((res) => {
        setInspections(res.data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };
  useEffect(() => {
    loadInspections();
  }, []);

  useEffect(() => {
    if (initialInspectionId) {
      openedFromDashboard.current = true;
      setActiveInspection(initialInspectionId);
    }
  }, [initialInspectionId]);

  const reopenAndEdit = (insp) => {
    if (insp.status === "in_progress") {
      setActiveInspection(insp.id);
      return;
    }
    if (insp.locked) {
      alert("This inspection is locked in Records and cannot be edited. Unlock it in Records first.");
      return;
    }
    axios.patch(`${API}/inspections/${insp.id}/`, { status: "in_progress" })
      .then(() => setActiveInspection(insp.id))
      .catch((err) => alert(err.response?.data?.detail || "Failed to reopen inspection."));
  };

  const filtered = inspections
    .filter((i) => showCompleted || i.status === "in_progress")
    .filter(
      (i) =>
        (i.site_name || "").toLowerCase().includes(search.toLowerCase()) ||
        (i.site_address || "").toLowerCase().includes(search.toLowerCase()) ||
        (i.inspector_name || "").toLowerCase().includes(search.toLowerCase()),
    );

  const typeLabel = (t) =>
    ({ monthly: "Monthly", bi_annual: "Bi-Annual", annual: "Annual" })[t] || t;
  const formatDate = (d) =>
    d
      ? new Date(d).toLocaleDateString("en-AU", {
          day: "2-digit",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        })
      : "—";

  if (activeInspection)
    return (
      <InspectionSheet
        inspectionId={activeInspection}
        onBack={() => {
          const wasDashboard = openedFromDashboard.current;
          openedFromDashboard.current = false;
          setActiveInspection(null);
          if (wasDashboard && onDone) {
            onDone();
          } else {
            loadInspections();
          }
        }}
      />
    );
  if (showCreate)
    return (
      <CreateInspection
        onBack={() => setShowCreate(false)}
        onCreate={(id) => {
          setShowCreate(false);
          setActiveInspection(id);
        }}
      />
    );

  return (
    <div>
      <div style={styles.topbar}>
        <div style={styles.pageTitle}>Inspections</div>
      </div>
      <div style={styles.content}>
        <div style={styles.card}>
          <div
            style={{
              ...styles.searchBar,
              flexDirection: isMobile ? "column" : "row",
              alignItems: isMobile ? "stretch" : "center",
              gap: isMobile ? "10px" : "12px",
            }}
          >
            <input
              style={{
                ...styles.searchInput,
                maxWidth: isMobile ? "100%" : "50%",
              }}
              placeholder="Search by site, address or technician..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {userRole !== "field_tech" && (
              <button
                style={styles.addButton}
                onClick={() => setShowCreate(true)}
              >
                + Create Inspection
              </button>
            )}
          </div>
          <div style={{ padding: "0 20px 12px" }}>
            <label style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", color: colors.text, cursor: "pointer" }}>
              <input type="checkbox" checked={showCompleted} onChange={(e) => setShowCompleted(e.target.checked)} />
              Show completed
            </label>
          </div>

          {loading ? (
            <div style={styles.loading}>Loading...</div>
          ) : filtered.length === 0 ? (
            <div style={styles.loading}>
              No inspections yet. Click + Create Inspection to get started.
            </div>
          ) : isMobile ? (
            /* Mobile card list */
            filtered.map((insp) => (
              <div
                key={insp.id}
                onClick={() => setActiveInspection(insp.id)}
                style={{
                  padding: "14px 16px",
                  borderBottom: `1px solid ${colors.border}`,
                  backgroundColor: hovered === insp.id ? "#fafafa" : "white",
                  cursor: "pointer",
                }}
                onTouchStart={() => setHovered(insp.id)}
                onTouchEnd={() => setHovered(null)}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    marginBottom: "6px",
                  }}
                >
                  <div
                    style={{
                      fontWeight: "600",
                      color: colors.text,
                      fontSize: "15px",
                      flex: 1,
                      paddingRight: "12px",
                    }}
                  >
                    {insp.site_name}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    {insp.locked && <LuLock size={13} title="Locked" style={{ color: colors.amber }} />}
                    <span style={styles.statusBadge(insp.status)}>
                      {insp.status === "in_progress"
                        ? "In Progress"
                        : insp.status === "passed"
                          ? "Passed"
                          : "Failed"}
                    </span>
                  </div>
                </div>
                <div style={{ fontSize: "12px", color: colors.slate, fontWeight: "500", marginBottom: "2px" }}>
                  {insp.site_area_name || "Whole Site"}
                </div>
                <div style={{ fontSize: "12px", color: "#999" }}>
                  {formatDate(insp.date)} · {typeLabel(insp.inspection_type)} ·{" "}
                  {insp.inspector_name}
                </div>
              </div>
            ))
          ) : (
            /* Desktop table */
            <>
              <div
                style={{
                  ...styles.tableHeader,
                  gridTemplateColumns: "2fr 1.3fr 1.5fr 1fr 1fr 1fr 70px",
                }}
              >
                <div>Site</div>
                <div>Section</div>
                <div>Date</div>
                <div>Type</div>
                <div>Technician</div>
                <div>Status</div>
                <div></div>
              </div>
              {filtered.map((insp) => (
                <div
                  key={insp.id}
                  style={{
                    ...styles.tableRow(hovered === insp.id),
                    gridTemplateColumns: "2fr 1.3fr 1.5fr 1fr 1fr 1fr 70px",
                  }}
                  onMouseEnter={() => setHovered(insp.id)}
                  onMouseLeave={() => setHovered(null)}
                  onClick={() => setActiveInspection(insp.id)}
                >
                  <div>
                    <div style={{ fontWeight: "500", color: colors.text }}>
                      {insp.site_name}
                    </div>
                    <div style={{ fontSize: "12px", color: "#999" }}>
                      {insp.site_address}
                    </div>
                  </div>
                  <div style={{ fontSize: "13px" }}>
                    {insp.site_area_name || "Whole Site"}
                  </div>
                  <div style={{ fontSize: "13px" }}>
                    {formatDate(insp.date)}
                  </div>
                  <div style={{ fontSize: "13px" }}>
                    {typeLabel(insp.inspection_type)}
                  </div>
                  <div style={{ fontSize: "13px" }}>{insp.inspector_name}</div>
                  <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    {insp.locked && <LuLock size={13} title="Locked" style={{ color: colors.amber }} />}
                    <span style={styles.statusBadge(insp.status)}>
                      {insp.status === "in_progress"
                        ? "In Progress"
                        : insp.status === "passed"
                          ? "Passed"
                          : "Failed"}
                    </span>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      gap: "6px",
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {userRole !== "field_tech" && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          reopenAndEdit(insp);
                        }}
                        title="Edit inspection"
                        style={{
                          display: "flex", alignItems: "center", justifyContent: "center",
                          width: "28px", height: "28px", borderRadius: "6px",
                          border: `1px solid ${colors.border}`, backgroundColor: "white",
                          color: colors.blue, cursor: "pointer",
                        }}
                      >
                        <LuPencil size={14} />
                      </button>
                    )}
                    {userRole !== "field_tech" && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (window.confirm("Delete this inspection?")) {
                            axios
                              .delete(`${API}/inspections/${insp.id}/`)
                              .then(() => loadInspections())
                              .catch(() => alert("Delete failed"));
                          }
                        }}
                        title="Delete inspection"
                        style={{
                          display: "flex", alignItems: "center", justifyContent: "center",
                          width: "28px", height: "28px", borderRadius: "6px",
                          border: `1px solid ${colors.border}`, backgroundColor: "white",
                          color: colors.red, cursor: "pointer",
                        }}
                      >
                        <LuTrash2 size={14} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </>
          )}
          <div style={styles.count}>
            {filtered.length} Inspection{filtered.length !== 1 ? "s" : ""}
          </div>
        </div>
      </div>
    </div>
  );
}
